import   _             from 'lodash';
import   Backend       from 'alto-utils/lib/backend';
import { extractLast } from 'alto-utils/lib/utils';

const ValidMatchers = [
  'in', 'any', 'all', 'icontains', 'contains', 'startswith', 'istartswith', 'endswith', 'iendswith',
  'year', 'month', 'day', 'week_day', 'regex', 'range', 'gt', 'lt', 'gte', 'lte', 'isnull', 'eq',
];

export default class RequestBuilder {
  constructor(backend, route, model, associations, includes, orders, pageNumber, pageSize, logRequests) {
    this.backend      = backend;
    this.route        = route;
    this.model        = model;
    this.associations = associations;
    this.includes     = includes;
    this.orders       = orders;
    this.pageNumber   = pageNumber;
    this.pageSize     = pageSize;
    this.logRequests  = logRequests;
  }

  static for(relation) {
    const params = _.at(relation,
      'backend', 'route', 'model', 'associations', 'includes', 'orders',
      'pageNumber', 'pageSize', 'logRequests'
    );

    return new (this)(...params);
  }

  index() {
    return this._ajax('GET', '', {
      params: _.concat(this._orderParams(), this._pageParams(), this._includeParams())
    });
  }

  filteredIndex(conditions) {
    const params = _.concat(
      this._condsToParams(conditions), this._orderParams(), this._pageParams(), this._includeParams()
    );

    return this._ajax('GET', '', { params });
  }

  get(pk) {
    return this._ajax('GET', '/' + pk, { params: this._includeParams() });
  }

  post(entity) {
    return this._ajax('POST', '', { body: this.model.serialize(entity) });
  }

  put(pk, attributes) {
    return this._ajax('PUT', '/' + pk, { body: this.model.serialize(attributes) });
  }

  patch(pk, attributes) {
    return this._ajax('PATCH', '/' + pk, { body: this.model.serialize(attributes) });
  }

  delete(pk) {
    return this._ajax('DELETE', '/' + pk);
  }

  postMany(entities) {
    return this._ajax('POST', '', { body: entities.map(this.model.serialize.bind(this.model)) });
  }

  _includeParams() {
    return this.includes.map(frontendPath =>
      ['include[]', this.associations.backendPathFor(this.model, frontendPath) + '.*']
    );
  }

  _orderParams() {
    return this.orders.map(ordering => ['sort[]', this._orderParamFor(ordering)]);
  }

  _condsToParams(conditions) {
    return _.flatMap(_.toPairs(conditions), ([key, val]) => {
      if (_.isArray(val)) {
        return val.map(v => [`filter{${this._filterKeyFor(key)}}`, v]);
      } else {
        return [[`filter{${this._filterKeyFor(key)}}`, val]];
      }
    });
  }

  _pageParams() {
    return [['page', this.pageNumber], ['page_size', this.pageSize]];
  }

  _orderParamFor(ordering) {
    let direction;

    if (_.startsWith(ordering, '-')) {
      direction = '-';
      ordering  = ordering.slice(1);
    } else {
      direction = '';
    }

    const [associations, attr] = extractLast(ordering.split('.'))

    let backendPath;

    if (associations.length) {
      backendPath = this._backendNestedFieldPathFor(associations.join('.'), attr);
    } else {
      backendPath = this.model.backendNameFor(attr);
    }

    return direction + backendPath;
  }

  _filterKeyFor(conditionKey) {
    const parts = _.split(conditionKey, /(\.|\|)/);

    let sign;

    if (_.startsWith(parts[0], '-')) {
      parts[0] = parts[0].slice(1);
      sign     = '-';
    } else {
      sign     = '';
    }

    let associations, attr;

    if (ValidMatchers.includes(_.last(parts))) {
      let backendPath, matcher, matcherSep, assocSep;

      associations                          = parts.slice(0, -4);
      [assocSep, attr, matcherSep, matcher] = parts.slice(-4);

      if (associations.length) {
        backendPath = this._backendNestedFieldPathFor(associations.join(''), assocSep, attr);
      } else {
        [attr, matcherSep, matcher] = parts;

        backendPath = this.model.backendNameFor(attr);
      }

      return [sign, backendPath, matcherSep, matcher].join('');
    } else { // without matcher
      let sep;

      associations = parts.slice(0, -2);
      [sep, attr]  = parts.slice(-2)

      if (associations.length) {
        return sign + this._backendNestedFieldPathFor(associations.join(''), sep, attr);
      } else {
        [attr] = parts;

        return sign + this.model.backendNameFor(attr);
      }
    }
  }

  _backendNestedFieldPathFor(assocPath, sep, attr) {
    const backendAssocPath = this.associations.backendPathFor(this.model, assocPath);
    const finalModel       = this.associations.finalModelFor( this.model, assocPath);
    const backendAttrName  = finalModel.backendNameFor(attr);

    return [backendAssocPath, backendAttrName].join(sep);
  }

  _ajax(method, path, { body = null, params = [] } = {}) {
    const url     = Backend.withParams(`${this.route}${path}`, params);
    const options = {};

    if (body) {
      options.body = body;
    }

    if (this.backend.auth) {
      if (!this.backend.auth.isLoggedIn) { throw new Error("you must log in to make request with auth"); }

      return this.backend.requestWithAuth(method, url, options, this.model.name, this.logRequests);
    } else {
      return this.backend.request(        method, url, options, this.model.name, this.logRequests);
    }
  }
}
